import React from "react"
import TextField from "@mui/material/TextField"
import Box from "@mui/material/Box"
import InputAdornment from "@mui/material/InputAdornment"
import Switch from "@mui/material/Switch"
import FormControlLabel from "@mui/material/FormControlLabel"
import { useForm, Controller, SubmitHandler } from "react-hook-form"
import Select from "@mui/material/Select"
import MenuItem from "@mui/material/MenuItem"
import InputLabel from "@mui/material/InputLabel"
import { FormControl } from "@mui/material"

import {
    DISTRIBUTION_FORM_SUPPLIERS,
    DISTRIBUTION_RATE_TABLE,
    CEZ_REGIONS,
    supplierHasHDO,
    distributionRateHasHDO,
    type DistributionElectricityFormInput,
} from "../../../types"
import CustomButton from "../../custom-button"
import { useCreateDistributionElectricity } from "../../../hooks/use-distribution-electricity"
import FormRow from "../helpers/form-row"

export default function CreateDistributionElectricityForm({
    estateId,
}: {
    estateId: string | number,
}) {
    const { control, watch, handleSubmit } = useForm<DistributionElectricityFormInput>({
        defaultValues: {
            supplier: '',
            distribution_rate: '',
            //has_manual_hdo: false, // backend fallbacks to true
            low_price_cents: '',
            high_price_cents: '',
            signal_code: '',
            region: 'zapad',
        },
    })

    const mutation = useCreateDistributionElectricity(estateId, () => { })

    const onSubmit: SubmitHandler<DistributionElectricityFormInput> = data => {
        mutation.mutate(data)
    }

    const supplier: DistributionElectricityFormInput['supplier'] | undefined = watch('supplier')

    const distribution_rate = watch('distribution_rate')

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
            }}>
                <FormRow>
                    <Controller
                        name="supplier"
                        control={control}
                        render={({ field }) => (
                            <FormControl sx={{ flex: 1 }}>
                                <InputLabel id="distribution-electricity-supplier-label">Distributor</InputLabel>
                                <Select
                                    labelId="distribution-electricity-supplier-label"
                                    id="distribution-electricity-supplier"
                                    label="Distributor"
                                    variant="standard"
                                    {...field}
                                >
                                    {DISTRIBUTION_FORM_SUPPLIERS.map(({ name, id }) => (
                                        <MenuItem key={id} value={id}>{name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            // <TextField id="distribution-electricity-supplier" label="Distributor" variant="standard" {...field} />
                        )}
                    />
                    <Controller
                        name="distribution_rate"
                        control={control}
                        render={({ field }) => (
                            <FormControl sx={{ flex: 1 }}>
                                <InputLabel id="distribution-electricity-distribution_rate">Distribuční sazba</InputLabel>
                                <Select
                                    labelId="distribution-electricity-distribution_rate"
                                    variant="standard"
                                    {...field}
                                >
                                    {DISTRIBUTION_RATE_TABLE.map(({ name, id }) => (
                                        <MenuItem key={id} value={id}>{name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    />
                </FormRow>
                {/* Temporarily hidden - sending always true */}
                {/* <Box mt={1}>
                    <FormControlLabel control={
                        <Controller
                        name="has_manual_hdo"
                        control={control}
                        render={({ field }) =>
                            <Switch
                                onChange={(e) => field.onChange(e.target.checked)}
                                checked={field.value}
                            />
                        }
                    />
                    } label="Manuální HDO" />
                </Box> */}
                <Controller
                    name="low_price_cents"
                    control={control}
                    render={({ field }) =>
                        <TextField
                            id="distribution-electricity-low-price"
                            label="Cena nízkého tarifu"
                            InputProps={{
                                endAdornment: <InputAdornment position="end">Kč bez DPH/MWh</InputAdornment>,
                            }}
                            variant="standard"
                            type="number"
                            disabled
                            aria-readonly
                            // required
                            {...field}
                        />
                    }
                />
                <Controller
                    name="high_price_cents"
                    control={control}
                    render={({ field }) =>
                        <TextField id="distribution-electricity-high-price"
                            label="Cena vysokého tarifu"
                            InputProps={{
                                endAdornment: <InputAdornment position="end">Kč bez DPH/MWh</InputAdornment>,
                            }}
                            variant="standard"
                            type="number"
                            disabled
                            aria-readonly
                            // required
                            {...field}
                        />}
                />

                {supplier === 'ČEZ' ?
                    <Box sx={{ display: distributionRateHasHDO(distribution_rate) ? 'flex' : 'none', flexDirection: 'column', gap: 1.5 }}>
                        <Controller
                            name="signal_code"
                            control={control}
                            render={({ field }) =>
                                <TextField
                                    id="distribution-electricity-signal-code"
                                    label="Kód HDO (např. A1B6DP1, PTV2)"
                                    variant="standard"
                                    required={false}
                                    {...field}
                                />
                            }
                        />
                        <Controller
                            name="region"
                            control={control}
                            render={({ field }) => (
                                <FormControl sx={{ flex: 1 }}>
                                    <InputLabel id="distribution-electricity-region-label">Region ČEZ</InputLabel>
                                    <Select
                                        labelId="distribution-electricity-region-label"
                                        id="distribution-electricity-region"
                                        label="Region ČEZ"
                                        variant="standard"
                                        {...field}
                                    >
                                        {CEZ_REGIONS.map(({ name, id }) => (
                                            <MenuItem key={id} value={id}>{name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        />
                    </Box>
                    :
                    <Box sx={{ display: supplierHasHDO(supplier) && distributionRateHasHDO(distribution_rate) ? 'flex' : 'none' }}>
                        <Controller
                            name="signal_code"
                            control={control}
                            render={({ field }) =>
                                <TextField
                                    id="distribution-electricity-signal-code"
                                    label="Kód HDO"
                                    variant="standard"
                                    required={false}
                                    {...field}
                                />
                            }
                        />
                    </Box>
                }

                <Box display="flex" justifyContent="center" mt={2} mb={2}>
                    <CustomButton
                        text="Přidat"
                        type="submit"
                        disabled={mutation.isPending}
                    />
                </Box>
                {mutation.isError ? (
                    <div>Došlo k chybě: {mutation.error.message}</div>
                ) : null}
            </Box>
        </form>
    )
}
